import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, Send, Copy, Check, Database, Mail, AlertCircle } from 'lucide-react';

const LOCALSTORAGE_ONDECK = 'emailgen_onDeck';
const LOCALSTORAGE_TONE = 'emailgen_tone';

const Field = ({ label, value, onChange, sub, width = '100%' }) => (
  <div style={{ flex: width === '100%' ? '1 1 100%' : `0 0 ${width}`, minWidth: 0 }}>
    <label style={{
      display: 'block', fontSize: '0.68rem', color: 'rgba(199, 210, 254, 0.5)',
      textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '0.25rem',
    }}>{label}</label>
    <input
      type="text"
      value={value ?? ''}
      onChange={e => onChange(e.target.value)}
      placeholder="—"
      style={{
        width: '100%', padding: '0.45rem 0.6rem', fontSize: '0.85rem',
        background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)',
        borderRadius: '5px', color: '#fff', outline: 'none', boxSizing: 'border-box',
      }}
    />
    {sub && <div style={{ fontSize: '0.65rem', color: 'rgba(199, 210, 254, 0.35)', marginTop: '0.15rem' }}>{sub}</div>}
  </div>
);

const SectionHeader = ({ children }) => (
  <h3 style={{
    color: '#fff', fontSize: '1rem', fontWeight: 700, margin: '1.5rem 0 0.75rem',
    paddingBottom: '0.4rem', borderBottom: '1px solid rgba(255,255,255,0.08)',
    display: 'flex', alignItems: 'center', gap: '0.5rem',
  }}>{children}</h3>
);

const StateRow = ({ data, onChange }) => {
  if (!data) return null;
  return (
    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', fontSize: '0.8rem', padding: '0.2rem 0' }}>
      <span style={{ color: 'rgba(199,210,254,0.7)', minWidth: '100px' }}>{data.state}</span>
      <input
        type="text" value={data.pct ?? ''} onChange={e => onChange({ ...data, pct: e.target.value })}
        style={{
          width: '70px', padding: '0.25rem 0.4rem', fontSize: '0.8rem',
          background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)',
          borderRadius: '4px', color: '#4ADE80', textAlign: 'center',
        }}
      />
    </div>
  );
};

const EmailGenerator = () => {
  const [loading, setLoading] = useState(false);
  const [loadingStep, setLoadingStep] = useState('');
  const [error, setError] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [emailOutput, setEmailOutput] = useState('');

  // KPI fields
  const [displayMonth, setDisplayMonth] = useState('');
  const [apps, setApps] = useState({});
  const [enrollments, setEnrollments] = useState({});
  const [hires, setHires] = useState({});

  // Manual input
  const [onDeck, setOnDeck] = useState(() => localStorage.getItem(LOCALSTORAGE_ONDECK) || '');
  const [toneNote, setToneNote] = useState(() => localStorage.getItem(LOCALSTORAGE_TONE) || '');

  // Persist manual inputs
  useEffect(() => { localStorage.setItem(LOCALSTORAGE_ONDECK, onDeck); }, [onDeck]);
  useEffect(() => { localStorage.setItem(LOCALSTORAGE_TONE, toneNote); }, [toneNote]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    setLoadingStep('Connecting to Salesforce...');
    try {
      const stepTimer = setInterval(() => {
        setLoadingStep(prev => {
          if (prev.includes('Connecting')) return 'Querying applications & enrollments...';
          if (prev.includes('Querying')) return 'Computing YoY metrics...';
          if (prev.includes('Computing')) return 'Finalizing data...';
          return prev;
        });
      }, 4000);

      const res = await fetch('/.netlify/functions/generate-email-data');
      clearInterval(stepTimer);

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error || `HTTP ${res.status}`);
      }
      const data = await res.json();

      setDisplayMonth(data.displayMonth || '');
      setApps(data.applications || {});
      setEnrollments(data.enrollments || {});
      setHires(data.hires || {});
    } catch (err) {
      console.error('Failed to fetch email data:', err);
      setError(err.message);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const generateEmail = async () => {
    setGenerating(true);
    setEmailOutput('');
    try {
      const res = await fetch('/.netlify/functions/draft-leadership-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          displayMonth,
          applications: apps,
          enrollments,
          hires,
          onDeck,
          toneNote,
        }),
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setEmailOutput(data.email || '');
    } catch (err) {
      console.error('Failed to generate email:', err);
      setEmailOutput(`Error: ${err.message}`);
    }
    setGenerating(false);
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(emailOutput);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback
      const ta = document.createElement('textarea');
      ta.value = emailOutput;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  // Updater helpers
  const updateApps = (key, val) => setApps(prev => ({ ...prev, [key]: val }));
  const updateEnroll = (key, val) => setEnrollments(prev => ({ ...prev, [key]: val }));
  const updateHires = (key, val) => setHires(prev => ({ ...prev, [key]: val }));
  const updateTopState = (idx, val) => {
    setApps(prev => {
      const topStates = [...(prev.topStates || [])];
      topStates[idx] = val;
      return { ...prev, topStates };
    });
  };

  if (loading) {
    return (
      <div className="hf-container">
        <div className="hf-map-card" style={{ padding: '3rem 2rem', textAlign: 'center' }}>
          <style>{`
            @keyframes emailPulse {
              0%, 100% { opacity: 0.4; transform: scaleX(0.3); }
              50% { opacity: 1; transform: scaleX(1); }
            }
            @keyframes emailSpin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}</style>
          <div style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            width: '48px', height: '48px', borderRadius: '50%',
            background: 'rgba(129, 140, 248, 0.15)', border: '1px solid rgba(129, 140, 248, 0.3)',
            animation: 'emailSpin 2s linear infinite', marginBottom: '1rem',
          }}>
            <Database size={22} style={{ color: '#818CF8' }} />
          </div>
          <div style={{ color: '#fff', fontSize: '0.95rem', fontWeight: 600, marginBottom: '0.5rem' }}>
            {loadingStep}
          </div>
          <div style={{
            width: '200px', height: '2px', margin: '0.75rem auto 0',
            background: 'rgba(129, 140, 248, 0.1)', borderRadius: '1px', overflow: 'hidden',
          }}>
            <div style={{
              width: '100%', height: '100%',
              background: 'linear-gradient(90deg, transparent, #818CF8, transparent)',
              animation: 'emailPulse 1.5s ease-in-out infinite',
              transformOrigin: 'center',
            }} />
          </div>
          <div style={{ color: 'rgba(199, 210, 254, 0.35)', fontSize: '0.7rem', marginTop: '0.75rem' }}>
            Querying Salesforce for monthly KPI data
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="hf-container">
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.25rem' }}>
        <div>
          <h2 style={{ color: '#fff', fontSize: '1.5rem', fontWeight: 800, margin: 0 }}>
            Monthly Leadership Email
          </h2>
          <p style={{ color: 'rgba(199, 210, 254, 0.7)', fontSize: '0.85rem', margin: '0.25rem 0 0 0' }}>
            Auto-populate KPIs from Salesforce, edit, and generate the draft
          </p>
        </div>
        <button onClick={fetchData} style={{
          display: 'flex', alignItems: 'center', gap: '0.4rem',
          background: 'rgba(0, 190, 168, 0.15)', border: '1px solid rgba(0, 190, 168, 0.3)',
          color: '#4ADE80', borderRadius: '6px', padding: '0.4rem 0.75rem',
          fontSize: '0.78rem', cursor: 'pointer',
        }}>
          <RefreshCw size={14} /> Refresh from Salesforce
        </button>
      </div>

      {/* Month display */}
      <div style={{
        background: 'rgba(129, 140, 248, 0.1)', border: '1px solid rgba(129, 140, 248, 0.2)',
        borderRadius: '8px', padding: '0.75rem 1rem', marginBottom: '1rem',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <div>
          <span style={{ color: 'rgba(199, 210, 254, 0.5)', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Reporting Month</span>
          <div style={{ color: '#818CF8', fontSize: '1.2rem', fontWeight: 700 }}>{displayMonth || '—'}</div>
        </div>
        {error && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', color: '#EF4444', fontSize: '0.8rem' }}>
            <AlertCircle size={16} /> {error}
          </div>
        )}
      </div>

      {/* SECTION 1: Applications */}
      <div className="hf-map-card">
        <SectionHeader>Applications</SectionHeader>
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
          <Field label="Current Month" value={apps.currentMonth} onChange={v => updateApps('currentMonth', v)} width="18%" />
          <Field label={`Prior Year (same month)`} value={apps.priorYearMonth} onChange={v => updateApps('priorYearMonth', v)} width="18%" />
          <Field label="YoY %" value={apps.yoyPct} onChange={v => updateApps('yoyPct', v)} width="12%" />
          <Field label="Forecast" value={apps.forecast} onChange={v => updateApps('forecast', v)} width="18%" />
          <Field label="vs Forecast %" value={apps.vsForecastPct} onChange={v => updateApps('vsForecastPct', v)} width="14%" />
        </div>
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginTop: '0.5rem' }}>
          <Field label="YTD" value={apps.ytd} onChange={v => updateApps('ytd', v)} width="18%" />
          <Field label="Prior YTD" value={apps.priorYtd} onChange={v => updateApps('priorYtd', v)} width="18%" />
          <Field label="YTD YoY %" value={apps.ytdYoyPct} onChange={v => updateApps('ytdYoyPct', v)} width="12%" />
          <Field label="Forecast YTD" value={apps.forecastYtd} onChange={v => updateApps('forecastYtd', v)} width="18%" />
          <Field label="YTD vs Forecast %" value={apps.ytdVsForecastPct} onChange={v => updateApps('ytdVsForecastPct', v)} width="14%" />
        </div>

        <div style={{ marginTop: '0.75rem' }}>
          <span style={{ fontSize: '0.7rem', color: 'rgba(199, 210, 254, 0.5)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Top 3 States (YoY Growth)
          </span>
          {(apps.topStates || []).map((s, i) => (
            <StateRow key={i} data={s} onChange={v => updateTopState(i, v)} />
          ))}
          {(!apps.topStates || apps.topStates.length === 0) && (
            <div style={{ fontSize: '0.75rem', color: 'rgba(199, 210, 254, 0.3)', padding: '0.3rem 0' }}>No state data loaded</div>
          )}
        </div>

        <div style={{ marginTop: '0.5rem' }}>
          <span style={{ fontSize: '0.7rem', color: 'rgba(199, 210, 254, 0.5)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Drag State
          </span>
          {apps.dragState ? (
            <StateRow data={apps.dragState} onChange={v => updateApps('dragState', v)} />
          ) : (
            <div style={{ fontSize: '0.75rem', color: 'rgba(199, 210, 254, 0.3)', padding: '0.3rem 0' }}>No drag state data</div>
          )}
        </div>
      </div>

      {/* SECTION 1: Enrollments */}
      <div className="hf-map-card">
        <SectionHeader>Enrollments</SectionHeader>
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
          <Field label="Current Month" value={enrollments.currentMonth} onChange={v => updateEnroll('currentMonth', v)} width="18%" />
          <Field label="Prior Year (same month)" value={enrollments.priorYearMonth} onChange={v => updateEnroll('priorYearMonth', v)} width="18%" />
          <Field label="YoY %" value={enrollments.yoyPct} onChange={v => updateEnroll('yoyPct', v)} width="12%" />
          <Field label="Forecast" value={enrollments.forecast} onChange={v => updateEnroll('forecast', v)} width="18%" />
          <Field label="vs Forecast %" value={enrollments.vsForecastPct} onChange={v => updateEnroll('vsForecastPct', v)} width="14%" />
        </div>
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginTop: '0.5rem' }}>
          <Field label="YTD" value={enrollments.ytd} onChange={v => updateEnroll('ytd', v)} width="18%" />
          <Field label="Prior YTD" value={enrollments.priorYtd} onChange={v => updateEnroll('priorYtd', v)} width="18%" />
          <Field label="YTD YoY %" value={enrollments.ytdYoyPct} onChange={v => updateEnroll('ytdYoyPct', v)} width="12%" />
          <Field label="Forecast YTD" value={enrollments.forecastYtd} onChange={v => updateEnroll('forecastYtd', v)} width="18%" />
          <Field label="YTD vs Forecast %" value={enrollments.ytdVsForecastPct} onChange={v => updateEnroll('ytdVsForecastPct', v)} width="14%" />
        </div>

        <div style={{ marginTop: '0.5rem' }}>
          <span style={{ fontSize: '0.7rem', color: 'rgba(199, 210, 254, 0.5)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Drag State
          </span>
          {enrollments.dragState ? (
            <StateRow data={enrollments.dragState} onChange={v => updateEnroll('dragState', v)} />
          ) : (
            <div style={{ fontSize: '0.75rem', color: 'rgba(199, 210, 254, 0.3)', padding: '0.3rem 0' }}>No drag state data</div>
          )}
        </div>
      </div>

      {/* SECTION 1: Hires */}
      <div className="hf-map-card">
        <SectionHeader>Hires</SectionHeader>
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
          <Field label={`${hires.fallSemester || 'Fall'} Hires`} value={hires.fallHires} onChange={v => updateHires('fallHires', v)} width="20%" />
          <Field label={`${hires.priorFallSemester || 'Prior Fall'} (same date)`} value={hires.priorFallHires} onChange={v => updateHires('priorFallHires', v)} width="20%" />
          <Field label="Fall YoY %" value={hires.fallYoyPct} onChange={v => updateHires('fallYoyPct', v)} width="15%" />
          <Field label="Total Teachers in Classrooms" value={hires.totalTeachers} onChange={v => updateHires('totalTeachers', v)} width="25%" />
        </div>
        <div style={{ color: 'rgba(199, 210, 254, 0.5)', fontSize: '0.72rem', marginTop: '0.5rem' }}>
          Field Experience records by FE semester — the same basis as the dashboard Hires card —
          counted as of {hires.asOf || 'the reporting month end'} and against the same date a year earlier.
          Most of a Fall cohort is keyed in after July, so both sides are deliberately part-season.
        </div>
        <div style={{ marginTop: '0.5rem' }}>
          <Field label="Hires Caveat Note" value={hires.caveatNote} onChange={v => updateHires('caveatNote', v)}
            sub="e.g., 'Data may lag due to district reporting delays'" />
        </div>
      </div>

      {/* SECTION 2: On Deck + Tone */}
      <div className="hf-map-card">
        <SectionHeader><Mail size={18} /> On Deck & Framing</SectionHeader>
        <div style={{ marginBottom: '1rem' }}>
          <label style={{
            display: 'block', fontSize: '0.72rem', color: 'rgba(199, 210, 254, 0.6)',
            marginBottom: '0.3rem', fontWeight: 600,
          }}>On Deck — what's coming next month</label>
          <textarea
            value={onDeck}
            onChange={e => setOnDeck(e.target.value)}
            placeholder="Strategic narrative for the On Deck section. Each initiative or focus area gets its own paragraph in the email..."
            rows={6}
            style={{
              width: '100%', padding: '0.6rem', fontSize: '0.85rem',
              background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: '6px', color: '#fff', resize: 'vertical', outline: 'none',
              lineHeight: 1.5, boxSizing: 'border-box',
            }}
          />
          <div style={{ fontSize: '0.65rem', color: 'rgba(199, 210, 254, 0.35)', marginTop: '0.2rem' }}>
            Persisted in localStorage — clears only when you clear it manually
          </div>
        </div>
        <div>
          <label style={{
            display: 'block', fontSize: '0.72rem', color: 'rgba(199, 210, 254, 0.6)',
            marginBottom: '0.3rem', fontWeight: 600,
          }}>Tone / Framing Note</label>
          <textarea
            value={toneNote}
            onChange={e => setToneNote(e.target.value)}
            placeholder='e.g., "KPIs are underwhelming but momentum is building" or "Strong month across the board"'
            rows={2}
            style={{
              width: '100%', padding: '0.6rem', fontSize: '0.85rem',
              background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: '6px', color: '#fff', resize: 'vertical', outline: 'none',
              lineHeight: 1.5, boxSizing: 'border-box',
            }}
          />
        </div>
      </div>

      {/* SECTION 3: Generate */}
      <div style={{ textAlign: 'center', margin: '1.5rem 0' }}>
        <button
          onClick={generateEmail}
          disabled={generating}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: '0.6rem',
            padding: '0.85rem 2.5rem', fontSize: '1rem', fontWeight: 700,
            background: generating ? 'rgba(129, 140, 248, 0.2)' : 'linear-gradient(135deg, #818CF8, #6366F1)',
            border: 'none', borderRadius: '8px', color: '#fff', cursor: generating ? 'wait' : 'pointer',
            boxShadow: generating ? 'none' : '0 4px 15px rgba(99, 102, 241, 0.4)',
            transition: 'all 0.2s ease',
          }}
        >
          {generating ? (
            <>
              <RefreshCw size={18} style={{ animation: 'emailSpin 1s linear infinite' }} />
              Drafting Email...
            </>
          ) : (
            <>
              <Send size={18} />
              Draft Email
            </>
          )}
        </button>
      </div>

      {/* Email Output */}
      {emailOutput && (
        <div className="hf-map-card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
            <h3 className="hf-section-title" style={{ margin: 0 }}>
              <Mail size={18} />
              Generated Email Draft
            </h3>
            <button
              onClick={copyToClipboard}
              style={{
                display: 'flex', alignItems: 'center', gap: '0.4rem',
                padding: '0.4rem 0.75rem', fontSize: '0.78rem',
                background: copied ? 'rgba(74, 222, 128, 0.2)' : 'rgba(0, 190, 168, 0.15)',
                border: `1px solid ${copied ? 'rgba(74, 222, 128, 0.4)' : 'rgba(0, 190, 168, 0.3)'}`,
                color: copied ? '#4ADE80' : '#4ADE80', borderRadius: '6px', cursor: 'pointer',
              }}
            >
              {copied ? <><Check size={14} /> Copied!</> : <><Copy size={14} /> Copy to Clipboard</>}
            </button>
          </div>
          <textarea
            readOnly
            value={emailOutput}
            style={{
              width: '100%', minHeight: '500px', padding: '1rem', fontSize: '0.88rem',
              background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)',
              borderRadius: '6px', color: 'rgba(199, 210, 254, 0.9)', resize: 'vertical',
              lineHeight: 1.7, fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box',
            }}
          />
        </div>
      )}
    </div>
  );
};

export default EmailGenerator;
